import React, { useState, useEffect } from 'react';
import { adminGetCompanyByOwnerId, adminListPackages } from '../../api/packagesApi';
import {
  resolveSubscriptionStatus,
  getCompanySubscription,
  getGracePeriodDays,
} from '../../../packages/utils/subscriptionStatus';
import {
  computeAnnualPrice,
  formatPackagePrice,
} from '../../../packages/utils/packagePricing';
import {
  applyCompanyPackage,
  removeCompanyPackage,
  expireCompanyPackage,
} from './companyPackageApi';
import './package-dialogs.css';

function useEscapeToClose(open, onClose) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
}

const ApplyPackageDialog = ({ open, onClose, companyId, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEscapeToClose(open, onClose);

  useEffect(() => {
    if (open) {
      fetchPackages();
      if (companyId) {
        fetchCompanyDetails();
      }
    }
  }, [companyId, open]);

  const fetchPackages = async () => {
    try {
      const packagesData = (await adminListPackages()).filter((pkg) => pkg.isActive);
      setPackages(packagesData);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const companyData = await adminGetCompanyByOwnerId(companyId);
      if (!companyData) {
        throw new Error('Company not found');
      }
      setCompany(companyData);

      const subscription = getCompanySubscription(companyData);
      const resolved = resolveSubscriptionStatus(subscription, getGracePeriodDays(companyData));
      setCurrentSubscription(subscription ? { ...subscription, ...resolved } : null);

      if (companyData.package?.id) {
        setSelectedPackage(companyData.package.id);
      }
    } catch (err) {
      console.error('Error fetching company details:', err);
      setError('Failed to load company details');
    }
  };

  const handleApplyPackage = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const selectedPkg = packages.find((pkg) => pkg.id === selectedPackage);
      if (!selectedPkg) {
        throw new Error('Selected package not found');
      }

      if (!company?.id) {
        throw new Error('Company not found');
      }

      await applyCompanyPackage({
        companyId: company.id,
        packageId: selectedPkg.id,
        billingCycle: selectedPkg.isAdminPackage ? undefined : selectedBillingCycle,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error applying package:', err);
      setError(err.message || 'Failed to apply package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemovePackage = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (!company?.id) {
        throw new Error('Company not found');
      }

      await removeCompanyPackage({ companyId: company.id });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error removing package:', err);
      setError('Failed to remove package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExpirePackage = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (!company?.id) {
        throw new Error('Company not found');
      }

      await expireCompanyPackage({ companyId: company.id });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error expiring package:', err);
      setError(`Failed to expire package: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getAnnualPrice = (pkg) => {
    const monthlyPrice = parseFloat(pkg.price?.monthly || 0);
    const annualDiscount = pkg?.subscription?.annualDiscount ?? 0;
    return computeAnnualPrice(monthlyPrice, annualDiscount).toFixed(2);
  };

  const selectedPkg = packages.find((pkg) => pkg.id === selectedPackage);

  if (!open) return null;

  return (
    <div
      className="pkg-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="apply-package-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="pkg-dialog">
        <header className="pkg-dialog-header">
          <h2 id="apply-package-title" className="pkg-dialog-title">Manage Package</h2>
        </header>

        <div className="pkg-dialog-body">
          {error && (
            <div className="pkg-dialog-alert pkg-dialog-alert--error" role="alert">
              {error}
            </div>
          )}

          {currentSubscription && (
            <section className="pkg-dialog-section">
              <span className="pkg-dialog-label">Current subscription status</span>
              <p className="pkg-dialog-text">
                {currentSubscription.isPermanent ? (
                  'Permanent Access (Admin Package)'
                ) : (
                  <>
                    Status: {currentSubscription.status}
                    {currentSubscription.daysUntilExpiration > 0 && (
                      <> ({currentSubscription.daysUntilExpiration} days remaining)</>
                    )}
                  </>
                )}
              </p>
              {!currentSubscription.isPermanent && (
                <>
                  {currentSubscription.isExpired && (
                    <div className="pkg-dialog-alert pkg-dialog-alert--error" style={{ marginTop: 10 }}>
                      Your subscription has expired. Please renew to restore service.
                    </div>
                  )}
                  <p className="pkg-dialog-muted" style={{ marginTop: 8 }}>
                    Next payment due:{' '}
                    {currentSubscription.nextPaymentDue?.seconds
                      ? new Date(currentSubscription.nextPaymentDue.seconds * 1000).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </>
              )}
            </section>
          )}

          {company && (
            <section className="pkg-dialog-section">
              <span className="pkg-dialog-label">Company details</span>
              <p className="pkg-dialog-text">{company.name}</p>
              <p className="pkg-dialog-muted">
                Current package: {company.package?.name || 'None'}
              </p>
            </section>
          )}

          <div className="pkg-dialog-field">
            <label htmlFor="apply-package-select">Select package</label>
            <select
              id="apply-package-select"
              className="pkg-dialog-select"
              value={selectedPackage}
              onChange={(e) => {
                setSelectedPackage(e.target.value);
                const pkg = packages.find((p) => p.id === e.target.value);
                if (pkg?.isAdminPackage) {
                  setSelectedBillingCycle('monthly');
                }
              }}
              disabled={loading || submitting}
            >
              <option value="">Choose a package…</option>
              {packages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name}
                  {pkg.isAdminPackage
                    ? ' — Permanent admin package'
                    : ` — $${formatPackagePrice(pkg.price?.monthly)}/mo`}
                </option>
              ))}
            </select>
          </div>

          {selectedPackage && !selectedPkg?.isAdminPackage && (
            <div className="pkg-dialog-field">
              <label htmlFor="apply-billing-cycle">Billing cycle</label>
              <select
                id="apply-billing-cycle"
                className="pkg-dialog-select"
                value={selectedBillingCycle}
                onChange={(e) => setSelectedBillingCycle(e.target.value)}
                disabled={submitting}
              >
                <option value="monthly">Monthly billing</option>
                {selectedPkg?.subscription?.annualDiscount > 0 && (
                  <option value="annually">
                    Annual billing (save {selectedPkg.subscription.annualDiscount}%)
                  </option>
                )}
              </select>
            </div>
          )}

          {selectedPkg && (
            <section className="pkg-dialog-section">
              <span className="pkg-dialog-label">Selected package details</span>
              <div className="pkg-dialog-details">
                <p className="pkg-dialog-price">
                  {selectedBillingCycle === 'monthly'
                    ? `$${formatPackagePrice(selectedPkg.price.monthly)}/month`
                    : `$${getAnnualPrice(selectedPkg)}/year`}
                  {selectedBillingCycle === 'annually' && (
                    <span className="pkg-dialog-save">
                      {' '}(Save {selectedPkg.subscription?.annualDiscount || 0}%)
                    </span>
                  )}
                </p>
                <p className="pkg-dialog-text">
                  Employees: {selectedPkg.limits.employees === -1 ? 'Unlimited' : selectedPkg.limits.employees}
                </p>
              </div>
            </section>
          )}
        </div>

        <footer className="pkg-dialog-footer">
          <button type="button" className="pkg-btn pkg-btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {company?.package && (
            <>
              <button
                type="button"
                className="pkg-btn pkg-btn-danger"
                onClick={handleExpirePackage}
                disabled={submitting}
              >
                Expire package
              </button>
              <button
                type="button"
                className="pkg-btn pkg-btn-warning"
                onClick={handleRemovePackage}
                disabled={submitting}
              >
                Remove package
              </button>
            </>
          )}
          <button
            type="button"
            className="pkg-btn pkg-btn-primary"
            onClick={handleApplyPackage}
            disabled={!selectedPackage || !company || submitting}
          >
            Apply package
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ApplyPackageDialog;
